import { makeAutoObservable, toJS } from 'mobx'

import { PassengersValues } from 'pages/avia/purchase-form/model'

import { BlackListAvia, BlackListPassenger } from 'shared/api/avia/black-list'
import { TicketInsurance } from 'shared/api/avia/insurance'
import { IScanDocument } from 'shared/api/scan-document'
import { TYPE_AGE } from 'shared/constants'
import { IPhoneInput } from 'shared/ui/phone-input'

interface SetPassengerData {
  index: number
  age: TYPE_AGE
  scannedData: IScanDocument
}

export interface FormPassengersValues {
  email: string
  confirm: boolean
  rememberMe?: boolean
  phone_number: IPhoneInput
  [TYPE_AGE.ADULTS]: PassengersValues[]
  [TYPE_AGE.CHILDREN]: PassengersValues[]
  [TYPE_AGE.INFANTS_WITHOUT_SEAT]: PassengersValues[]
  // [TYPE_AGE.INFANTS_WITH_SEAT]: PassengersValues[]
}

class FormPassengersStore {
  formPassengersStore: FormPassengersValues | null = null
  initialFormValuesSnapshot: string | null = null

  ticketInsurances: TicketInsurance[] | null = null
  selectedInsurances: TicketInsurance[] | null = null
  insuranceTariffCondition: string | null = null
  insuranceErrorMessage: string | null = null
  abortController: AbortController | null = null

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true })
  }

  setValuesForm(data: FormPassengersValues) {
    this.formPassengersStore = data
  }

  initializeFormSnapshot(data: FormPassengersValues) {
    if (this.initialFormValuesSnapshot !== null) return

    this.initialFormValuesSnapshot = JSON.stringify({
      ...data,
      confirm: false,
    })
  }

  hasFormChanges(data: FormPassengersValues) {
    if (this.initialFormValuesSnapshot === null) return false

    return (
      this.initialFormValuesSnapshot !==
      JSON.stringify({ ...data, confirm: false })
    )
  }

  clearValuesForm() {
    this.formPassengersStore = null
    this.initialFormValuesSnapshot = null
  }

  setPassengerData({ index, age, scannedData }: SetPassengerData) {
    if (!this.formPassengersStore) {
      return
    }

    this.formPassengersStore[age][index] = {
      passport: scannedData?.document_type || '',
      firstName: scannedData?.first_name || '',
      lastName: scannedData?.last_name || '',
      gender: scannedData?.gender || '',
      dateOfBirth: scannedData?.date_of_birth || '',
      citizenship: scannedData?.citizenship?.code || '',
      serialDocuments: scannedData?.id_card_number || '',
      documentExpirationDate: scannedData?.id_card_expire || '',
      doesMiddleName: false,
      middleName: '',
    }
  }

  setTicketInsurances(data: TicketInsurance[]) {
    this.ticketInsurances = data
    this.selectedInsurances = data
    this.insuranceErrorMessage = null
  }

  setSelectedInsurance(data: TicketInsurance) {
    if (this.selectedInsurances) {
      return (this.selectedInsurances = [...this.selectedInsurances!, data])
    }

    this.selectedInsurances = [data]
  }

  setInsuranceTariffCondition(data: string) {
    this.insuranceTariffCondition = data
  }

  setInsuranceErrorMessage(data: string | null) {
    this.insuranceErrorMessage = data
  }

  resetTicketInsurances() {
    this.ticketInsurances = null
    this.insuranceTariffCondition = null
    this.selectedInsurances = null
    this.insuranceErrorMessage = null
  }

  removeInsurance(name: string) {
    if (!this.selectedInsurances) {
      return
    }
    this.selectedInsurances = this.selectedInsurances.filter(
      (item) => item.name !== name,
    )
  }

  calculateInsurancePrice() {
    return (
      this.selectedInsurances
        ?.reduce((total, insurance) => total + parseFloat(insurance.price), 0)
        .toString() || '0'
    )
  }

  async getBlackListPassengers(passengers: BlackListPassenger[]) {
    try {
      this.abortController?.abort()

      this.abortController = new AbortController()

      BlackListAvia.checkBlackList(passengers, this.abortController?.signal)
    } catch (e) {
      throw e
    }
  }

  getListOfPassengers() {
    const allPassengers = [
      ...(this.formPassengersStore?.[TYPE_AGE.ADULTS] || []),
      ...(this.formPassengersStore?.[TYPE_AGE.CHILDREN] || []),
      ...(this.formPassengersStore?.[TYPE_AGE.INFANTS_WITHOUT_SEAT] || []),
    ]

    return toJS(allPassengers)
  }
}

export default new FormPassengersStore()
